import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { BUILD, LIGHTING } from '../config';
import { makePiece } from '../game/building';
import { S } from '../game/build/grid';
import { wallSlotHeight } from '../game/build/catalog';
import { buildHumanoid } from '../game/player';
import { sharedEnvironment } from '../engine/envlight';
import { onGLBReady } from '../engine/gltf';
import { spriteMat } from '../game/dropped';
import { TH, el, tBtn } from './theme';

// F4 -> OBJECTS: a live 3D calibration rig (owner only). One stone foundation + 2 full walls (a back
// corner) + a half-wall on the opposite edge (so half-wall height is directly comparable) + a ceiling,
// plus a player-height reference body standing on the floor -- all built from the SAME piece geometry
// gameplay places (building.ts's makePiece, player.ts's buildHumanoid), not a hand-approximated copy.
// The selected buildable GLB or item icon sprite sits in the open corner with SCALE (what we're really
// here for) and MOVE (testing convenience only, never saved) controls, and a Save button that writes the
// chosen scale into public/data/buildable_resizers.json or item_resizers.json via the dev-only
// /__save_resizer endpoint (vite.config.ts) -- those files are what engine/resizers.ts reads at runtime.

export type LabMode = 'buildables' | 'icons';

const RIG_TIER = 'stone' as const;
const FOUND_TOP = BUILD.foundDepth;                          // world Y of the walkable floor
const WALL_TOP = FOUND_TOP + BUILD.wallHeight;                // world Y of the wall-top / ceiling underside
const CEIL_Y = WALL_TOP + BUILD.ceilingThick / 2;
const HALF_WALL_Y = FOUND_TOP + wallSlotHeight('half_wall') / 2;
const FULL_WALL_Y = FOUND_TOP + BUILD.wallHeight / 2;
const EDGE = S / 2;
const DEFAULT_POS = { buildables: new THREE.Vector3(0, FOUND_TOP, 0), icons: new THREE.Vector3(0, FOUND_TOP + 0.1, 0) };

export class ObjectLab {
  private readonly canvas = document.createElement('canvas');
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.05, 200);
  private readonly controls: OrbitControls;
  private readonly testSlot = new THREE.Group();
  private raf = 0;
  private mode: LabMode = 'buildables';
  private currentKey: string | null = null;
  private savedBuildables: Record<string, number> = {};
  private savedItems: Record<string, number> = {};
  private modelListCache: string[] | null = null;

  // UI refs, built once and reused across mount/unmount.
  private readonly root: HTMLElement;
  private readonly viewportWrap: HTMLElement;
  private readonly statusEl: HTMLElement;
  private readonly nameEl: HTMLElement;
  private readonly scaleRange: HTMLInputElement;
  private readonly scaleNum: HTMLInputElement;
  private readonly posX: HTMLInputElement;
  private readonly posY: HTMLInputElement;
  private readonly posZ: HTMLInputElement;
  onSelect: ((key: string) => void) | null = null; // AdminPage hooks this to re-paint the palette selection

  constructor() {
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.AgXToneMapping;
    this.renderer.toneMappingExposure = LIGHTING.toneExposure;
    this.renderer.shadowMap.enabled = true;

    this.scene.environment = sharedEnvironment(this.renderer); // metals render black without this (MODELING.md)
    this.scene.environmentIntensity = 0.9;
    this.scene.background = new THREE.Color(0x8fb8d8);
    this.scene.fog = new THREE.Fog(0x8fb8d8, 20, 60);
    this.scene.add(new THREE.HemisphereLight(0xdcebff, 0x3a4a2e, 1.1));
    const sun = new THREE.DirectionalLight(0xfff2da, 2.2);
    sun.position.set(6, 12, 5);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.left = -8; sun.shadow.camera.right = 8; sun.shadow.camera.top = 8; sun.shadow.camera.bottom = -8;
    this.scene.add(sun);

    const ground = new THREE.Mesh(new THREE.PlaneGeometry(60, 60), new THREE.MeshStandardMaterial({ color: 0x4a6a38, roughness: 0.95 }));
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);
    this.scene.add(this.buildRig());
    this.scene.add(this.testSlot);

    // Framed so the WHOLE rig (foundation through ceiling, ~6.7m tall) fits comfortably on first open --
    // the foundation alone is 3.375m, easy to under-frame if you eyeball it against the old procedural pieces.
    this.camera.position.set(9, 6.5, 10.5);
    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.target.set(0, WALL_TOP * 0.5, 0);
    this.controls.update();

    // ---- control panel (built once; theme.ts tokens match the rest of AdminPage) ----
    this.viewportWrap = el('div', `position:relative;width:100%;height:56vh;min-height:380px;border:1px solid ${TH.line};border-radius:${TH.r};overflow:hidden;background:#8fb8d8`);
    this.canvas.style.cssText = 'width:100%;height:100%;display:block';
    this.viewportWrap.appendChild(this.canvas);

    this.nameEl = el('div', `font:700 ${TH.f4} system-ui;color:${TH.text}`, 'Pick something from the list on the left');
    this.statusEl = el('div', `font:${TH.f2} system-ui;color:${TH.dim};min-height:16px`);

    this.scaleRange = document.createElement('input');
    this.scaleRange.type = 'range'; this.scaleRange.min = '0.02'; this.scaleRange.max = '4'; this.scaleRange.step = '0.005';
    this.scaleRange.style.cssText = 'flex:1;accent-color:' + TH.rust;
    this.scaleNum = numInput(64);
    const syncFromRange = (): void => { this.scaleNum.value = this.scaleRange.value; this.applyScale(parseFloat(this.scaleRange.value)); };
    const syncFromNum = (): void => { const v = parseFloat(this.scaleNum.value); if (!isNaN(v)) { this.scaleRange.value = String(v); this.applyScale(v); } };
    this.scaleRange.addEventListener('input', syncFromRange);
    this.scaleNum.addEventListener('input', syncFromNum);
    const scaleRow = el('div', 'display:flex;align-items:center;gap:10px');
    scaleRow.append(el('div', `width:70px;font:700 ${TH.f2} system-ui;color:${TH.dim};letter-spacing:0.5px`, 'SCALE'), this.scaleRange, this.scaleNum);

    this.posX = numInput(58); this.posY = numInput(58); this.posZ = numInput(58);
    for (const [inp, axis] of [[this.posX, 'x'], [this.posY, 'y'], [this.posZ, 'z']] as const) {
      inp.addEventListener('input', () => { const v = parseFloat(inp.value); if (!isNaN(v)) this.testSlot.position[axis] = v; });
    }
    const resetPos = tBtn('↺ Reset position', () => this.setPosition(DEFAULT_POS[this.mode]));
    const moveRow = el('div', 'display:flex;align-items:center;gap:10px;flex-wrap:wrap');
    moveRow.append(el('div', `width:70px;font:700 ${TH.f2} system-ui;color:${TH.dim};letter-spacing:0.5px`, 'MOVE'),
      labelled('X', this.posX), labelled('Y', this.posY), labelled('Z', this.posZ), resetPos,
      el('div', `font:${TH.f1} system-ui;color:${TH.faint}`, '(scene testing only -- never saved)'));

    const saveBtn = tBtn('💾 Save scale', () => void this.save(), 'primary');
    const copyBtn = tBtn('⧉ Copy JSON', () => void this.copy());
    const actionRow = el('div', 'display:flex;align-items:center;gap:10px');
    actionRow.append(saveBtn, copyBtn, this.statusEl);

    const panel = el('div', `display:flex;flex-direction:column;gap:12px;margin-top:${TH.s4};padding:${TH.s4};background:${TH.panel};border:1px solid ${TH.line};border-radius:${TH.r}`);
    panel.append(this.nameEl, scaleRow, moveRow, actionRow);

    this.root = el('div', 'display:flex;flex-direction:column;min-width:0');
    this.root.append(this.viewportWrap, panel);
  }

  // A back-corner nook: BACK wall (+ -Z edge) and LEFT wall (-X edge) full height, a HALF-WALL on the
  // FRONT edge (opposite the back wall, so half-wall height is directly comparable), a ceiling overhead,
  // and a player-height reference body standing in the back corner. The right/front-right stays open for
  // the camera and for the test object.
  private buildRig(): THREE.Group {
    const g = new THREE.Group();
    const found = makePiece('foundation_square', false, RIG_TIER);
    found.position.set(0, FOUND_TOP - BUILD.foundDepth / 2, 0); // makePiece centres the block on its own origin
    g.add(found);
    const back = makePiece('wall', false, RIG_TIER);
    back.position.set(0, FULL_WALL_Y, -EDGE);
    g.add(back);
    const left = makePiece('wall', false, RIG_TIER);
    left.position.set(-EDGE, FULL_WALL_Y, 0);
    left.rotation.y = Math.PI / 2;
    g.add(left);
    const front = makePiece('half_wall', false, RIG_TIER);
    front.position.set(0, HALF_WALL_Y, EDGE);
    g.add(front);
    const ceiling = makePiece('ceiling_square', false, RIG_TIER);
    ceiling.position.set(0, CEIL_Y, 0);
    g.add(ceiling);
    const { body: player } = buildHumanoid();
    player.position.set(-EDGE + 0.55, FOUND_TOP, -EDGE + 0.55); // back-left corner, out of the test object's way
    player.rotation.y = -Math.PI / 4;
    g.add(player);
    g.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; } });
    return g;
  }

  // ---- lifecycle ----
  mount(host: HTMLElement, mode: LabMode): void {
    this.mode = mode;
    host.appendChild(this.root);
    this.resize();
    if (!this.raf) this.loop();
  }
  unmount(): void {
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
    this.root.remove();
  }
  private resize(): void {
    const w = this.viewportWrap.clientWidth || 600, h = this.viewportWrap.clientHeight || 400;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }
  private loop = (): void => {
    this.raf = requestAnimationFrame(this.loop);
    this.resize();
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  };

  // ---- data (model list + saved scales) ----
  async listModels(): Promise<string[]> {
    if (this.modelListCache) return this.modelListCache;
    try {
      const res = await fetch('/__list_models');
      const j = (await res.json()) as { files: string[] };
      this.modelListCache = j.files;
    } catch { this.modelListCache = []; }
    return this.modelListCache;
  }
  private async loadSaved(): Promise<void> {
    const load = async (url: string): Promise<Record<string, number>> => { try { const r = await fetch(url); return r.ok ? (await r.json()) as Record<string, number> : {}; } catch { return {}; } };
    this.savedBuildables = await load('/data/buildable_resizers.json');
    this.savedItems = await load('/data/item_resizers.json');
  }

  // ---- selection ----
  async select(key: string): Promise<void> {
    if (!Object.keys(this.savedBuildables).length && !Object.keys(this.savedItems).length) await this.loadSaved(); // first pick in the session: fetch the current saves once
    this.currentKey = key;
    this.nameEl.textContent = key;
    this.testSlot.clear();
    this.setPosition(DEFAULT_POS[this.mode]);
    const startScale = this.mode === 'buildables' ? (this.savedBuildables[key] ?? 1) : (this.savedItems[key] ?? 0.55);
    if (this.mode === 'buildables') {
      this.statusEl.textContent = 'loading…';
      onGLBReady('/models/' + key, (clone) => {
        if (this.currentKey !== key) return; // a different pick landed first
        // Most raw GLBs pivot at their own volumetric centre, not base-anchored like the game's primitive
        // pieces (see craftables.ts's fitWaterCatcherTemplate) -- recentre horizontally and rest the
        // bottom on the wrap's own y=0 so it sits naturally on the foundation like it will in-game,
        // instead of floating half-buried. MOVE still overrides this for deliberate off-floor testing.
        const box = new THREE.Box3().setFromObject(clone);
        const center = new THREE.Vector3(); box.getCenter(center);
        clone.position.set(-center.x, -box.min.y, -center.z);
        const wrap = new THREE.Group();
        wrap.add(clone);
        this.testSlot.add(wrap);
        clone.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; } });
        this.statusEl.textContent = '';
        this.setScaleControl(startScale);
      });
    } else {
      const s = new THREE.Sprite(spriteMat(key));
      this.testSlot.add(s);
      this.setScaleControl(startScale);
    }
    this.onSelect?.(key);
  }
  private setScaleControl(v: number): void { this.scaleRange.value = String(v); this.scaleNum.value = String(v); this.applyScale(v); }
  private applyScale(v: number): void { this.testSlot.scale.setScalar(Math.max(0.001, v)); }
  private setPosition(p: THREE.Vector3): void { this.testSlot.position.copy(p); this.posX.value = p.x.toFixed(2); this.posY.value = p.y.toFixed(2); this.posZ.value = p.z.toFixed(2); }

  // ---- save / copy ----
  private async save(): Promise<void> {
    if (!this.currentKey) return;
    const v = parseFloat(this.scaleNum.value);
    if (isNaN(v)) return;
    const file = this.mode === 'buildables' ? 'buildable_resizers.json' : 'item_resizers.json';
    const map = this.mode === 'buildables' ? this.savedBuildables : this.savedItems;
    map[this.currentKey] = v;
    try {
      const res = await fetch('/__save_resizer', { method: 'POST', body: JSON.stringify({ file, data: map }) });
      const j = (await res.json()) as { ok?: boolean };
      if (!j.ok) throw new Error('save failed');
      this.statusEl.textContent = `Saved ${this.currentKey} = ${v} -> public/data/${file}`;
      this.statusEl.style.color = TH.good;
    } catch {
      await this.copy();
      this.statusEl.textContent = 'Dev save endpoint not available -- copied JSON instead (restart the dev server after pulling, or paste this to Claude Code).';
      this.statusEl.style.color = TH.warn;
    }
  }
  private async copy(): Promise<void> {
    if (!this.currentKey) return;
    const v = parseFloat(this.scaleNum.value);
    const text = JSON.stringify({ [this.currentKey]: isNaN(v) ? 1 : v }, null, 2);
    try { await navigator.clipboard.writeText(text); this.statusEl.textContent = 'Copied to clipboard.'; this.statusEl.style.color = TH.dim; }
    catch { this.statusEl.textContent = text; this.statusEl.style.color = TH.dim; } // clipboard blocked: show it inline so it can be selected by hand
  }
}

function numInput(w: number): HTMLInputElement {
  const i = el('input', `width:${w}px;padding:6px 8px;background:${TH.sunk};border:1px solid ${TH.line};border-radius:${TH.rsm};color:${TH.rustHi};font:600 ${TH.f3} ui-monospace,monospace;text-align:right;box-shadow:${TH.inset}`);
  i.type = 'text'; i.inputMode = 'decimal';
  return i;
}
function labelled(letter: string, input: HTMLInputElement): HTMLElement {
  const w = el('div', 'display:flex;align-items:center;gap:4px');
  w.append(el('span', `font:700 ${TH.f1} system-ui;color:${TH.faint}`, letter), input);
  return w;
}
